import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, useApi } from '../api';

/**
 * Legal pages. The text below is a plain-language TEMPLATE for a prepaid PostgreSQL hosting service.
 * Replace the bracketed placeholders and have counsel review before launch; it is not legal advice.
 */
const COMPANY = '[Company legal name]';
const JURISDICTION = '[State/Country]';
const CONTACT = '[legal@example.com]';

export function LegalPage({ kind }: { kind: 'terms' | 'privacy' }) {
  return (
    <div className="landing">
      <header className="landing-nav">
        <Link to="/" className="brand" style={{ margin: 0 }}>
          TallyPG
        </Link>
        <nav>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/login">Sign in</Link>
        </nav>
      </header>
      <div className="card legal">{kind === 'terms' ? <Terms /> : <Privacy />}</div>
    </div>
  );
}

function Terms() {
  return (
    <>
      <h1>Terms of Service</h1>
      <p className="muted">
        Version {TERMS_VERSION_LABEL}. These terms are between you and {COMPANY} ("TallyPG", "we").
      </p>
      <h3>1. The service</h3>
      <p>
        TallyPG provides hosted PostgreSQL databases. You may create databases, connect to them with standard
        PostgreSQL tools, and manage them through the dashboard and API.
      </p>
      <h3>2. Accounts</h3>
      <p>
        You must provide accurate information, keep your credentials and API keys confidential, and are
        responsible for all activity under your account. One person may not hold multiple trial accounts.
      </p>
      <h3>3. Prepaid credit and billing</h3>
      <p>
        Usage is billed from a prepaid balance at the published rates for storage, database activity and
        network transfer. New accounts receive promotional credit that expires after the stated period. Paid
        credit does not expire. Bonus credit is promotional and non-refundable. Purchased credit is refundable
        only for the unused balance, on request, minus payment-provider fees, within 30 days of purchase.
      </p>
      <h3>4. Balance exhaustion and data retention</h3>
      <p>
        When your balance reaches zero, connections to your databases are paused and your data is retained for
        at least {RETENTION_DAYS} days. Adding credit resumes service. Data may be permanently deleted after
        the retention period; we will notify the email on file first.
      </p>
      <h3>5. Acceptable use</h3>
      <p>
        You may not use the service to store or process unlawful content, to attack other systems, to send
        unsolicited messages, to mine cryptocurrency, or to circumvent the trial. We may suspend accounts that
        endanger the service or other customers and will tell you why when we can.
      </p>
      <h3>6. Your data</h3>
      <p>
        Your data is yours. We access it only to operate the service, to comply with law, or with your
        permission. You are responsible for backups of data you cannot afford to lose; you can export at any
        time with standard PostgreSQL tools.
      </p>
      <h3>7. Availability and support</h3>
      <p>
        We aim for continuous availability but provide the service "as is" without a guaranteed uptime level.
        Support is by email at {CONTACT}.
      </p>
      <h3>8. Liability</h3>
      <p>
        To the extent permitted by law, our total liability for any claim is limited to the amount you paid us
        in the 12 months before the claim. We are not liable for indirect or consequential loss, including
        lost data or profits.
      </p>
      <h3>9. Changes and termination</h3>
      <p>
        We may update these terms; you will be asked to accept the new version before continuing to use the
        dashboard. You may close your account at any time. We may terminate for breach with notice where
        practical.
      </p>
      <h3>10. Governing law</h3>
      <p>These terms are governed by the laws of {JURISDICTION}.</p>
    </>
  );
}

function Privacy() {
  return (
    <>
      <h1>Privacy Policy</h1>
      <p className="muted">
        Version {TERMS_VERSION_LABEL}. {COMPANY} is the data controller.
      </p>
      <h3>What we collect</h3>
      <p>
        Account details (name, email, password hash), sign-in records (time, IP address, browser), usage and
        billing records for your databases, payment references from our payment provider (we never see full
        card or bank numbers), and support correspondence.
      </p>
      <h3>Why</h3>
      <p>
        To provide and bill the service, keep it secure, prevent abuse, and comply with law. We do not sell
        personal data and do not use it for advertising.
      </p>
      <h3>Database contents</h3>
      <p>
        The data you store in your databases is processed on your behalf. We do not read it except as needed
        to operate the service, investigate abuse, or as required by law.
      </p>
      <h3>Processors</h3>
      <p>
        Payment processing (Stripe), infrastructure hosting, email delivery, and the edge network that serves
        the dashboard. Each processes data only under contract with us.
      </p>
      <h3>Retention</h3>
      <p>
        Account and billing records are kept for as long as your account exists and for the period required by
        tax and accounting law. Database contents follow the retention rules in the terms.
      </p>
      <h3>Your rights</h3>
      <p>
        You can access, export, correct or delete your personal data by contacting {CONTACT}. You can delete
        databases yourself at any time.
      </p>
      <h3>Contact</h3>
      <p>{CONTACT}</p>
    </>
  );
}

const TERMS_VERSION_LABEL = '2026-09-03';
const RETENTION_DAYS = 90;

/** Shown after signup and whenever the terms version changes. */
export function AgreementPage() {
  const nav = useNavigate();
  const terms = useApi<{ version: string; accepted: boolean; termsUrl: string; privacyUrl: string }>(
    '/account/terms',
  );
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (terms.data?.accepted)
    return (
      <p className="muted">
        Thanks, you are all set. <Link to="/">Go to the dashboard</Link>.
      </p>
    );
  return (
    <div className="auth" style={{ maxWidth: 560 }}>
      <div className="card">
        <h1>One more step</h1>
        <p className="muted">
          Before you create databases, please read and accept our terms of service and privacy policy.
        </p>
        <ul>
          <li>
            <a href="/terms" target="_blank" rel="noreferrer">
              Terms of Service
            </a>{' '}
            (version {terms.data?.version ?? '…'})
          </li>
          <li>
            <a href="/privacy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
          </li>
        </ul>
        <label style={{ marginTop: 10 }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ width: 'auto', marginRight: 8 }}
          />
          I have read and agree to the Terms of Service and the Privacy Policy
        </label>
        {error && <p className="error">{error}</p>}
        <button
          style={{ marginTop: 12 }}
          disabled={!checked || !terms.data}
          onClick={async () => {
            try {
              await api('/account/accept-terms', {
                method: 'POST',
                json: { version: terms.data!.version, accepted: true },
              });
              const ref = localStorage.getItem('tallypg_ref');
              if (ref) {
                localStorage.removeItem('tallypg_ref');
                await api('/referrals/claim', { method: 'POST', json: { code: ref } }).catch(() => undefined);
              }
              const returnTo = localStorage.getItem('tallypg_return_to');
              localStorage.removeItem('tallypg_return_to');
              nav(returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/', {
                replace: true,
              });
              window.location.reload();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed');
            }
          }}
        >
          Agree and continue
        </button>
      </div>
    </div>
  );
}
